import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, Typography, Container, Link as MuiLink, Grid, Divider, useMediaQuery } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ReceiptSnapwhiltelogo from '../assets/ReceiptSnapwhiltelogo.png'


const Footer = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const primaryColorHex = '#007aff';
    const secondaryColorHex = '#010066';
    const backgroundAlpha = 0.9;
    const primaryColorRgba = alpha(primaryColorHex, backgroundAlpha);
    const secondaryColorRgba = alpha(secondaryColorHex, backgroundAlpha);

    const linkStyle = {
        color: 'common.white',
        textDecoration: 'none',
        mb: { xs: 0.8, sm: 1 },
        display: 'block',
        transition: 'opacity 0.2s ease-in-out',
        fontSize: { xs: '0.8rem', sm: '0.875rem' },
        '&:hover': {
            opacity: 0.85,
        }
    };

    const bottomLinkStyle = {
        color: 'common.white',
        textDecoration: 'none',
        mx: { xs: 0.5, sm: 1 },
        fontSize: '0.8rem',
        transition: 'opacity 0.2s ease-in-out',
        '&:hover': {
            opacity: 0.85,
        }
    };

    const contactItemStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.2 },
        mb: { xs: 0.8, sm: 1 },
        color: 'common.white',
        fontSize: { xs: '0.8rem', sm: '0.875rem' },
    };

    const headingStyle = {
        fontWeight: 'bold',
        mb: { xs: 1, sm: 1.5 },
        color: 'common.white',
        fontSize: { xs: '0.95rem', sm: '1rem' },
        '&::before': {
            content: '"|"',
            marginRight: '6px',
            color: theme.palette.common.white,
            fontWeight: 'bold',
        },
    };

    const handleSamePageLinkClick = (targetPath) => {
        if (location.pathname === targetPath) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };
    const isLoggedIn = !!localStorage.getItem('user_id');

    // App Store redirect handler
    const handleStoreClick = (event) => {
        if (event && event.preventDefault) event.preventDefault();
        window.open('https://apps.apple.com/us/app/receiptsnap-ai/id6753581452', '_blank', 'noopener,noreferrer');
    };

    return (
        <Box
            component="footer"
            sx={{
                fontFamily: "'Inter', sans-serif",
                mt: 'auto',
                background: `linear-gradient(135deg, ${primaryColorRgba} 0%, ${secondaryColorRgba} 100%)`,
                color: 'common.white',
                py: { xs: 3, sm: 4, md: 5 },
                px: { xs: 2, sm: 3, md: 4, lg: 5 },
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
                <Grid container spacing={{ xs: 3, sm: 4, md: 5 }} justifyContent="space-between">
                    <Grid item xs={12} md={5} lg={4}>
                        <MuiLink
                            component={NavLink}
                            to="/home"
                            sx={{ display: 'inline-block', mb: 2 }}
                            onClick={() => handleSamePageLinkClick('/home')}
                        >
                            <img
                                src={ReceiptSnapwhiltelogo}
                                alt="ReceiptSnap Logo"
                                style={{
                                    height: isMobile ? '50px' : '60px',
                                    width: 'auto',
                                    display: 'block'
                                }}
                            />
                        </MuiLink>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2, color: 'common.white',
                                lineHeight: 1.5,
                                maxWidth: { xs: '100%', md: '350px' },
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            }}
                        >
                            Streamline your expense tracking. Store, and manage receipts effortlessly with AI-powered accuracy.
                        </Typography>
                    </Grid>
                  {isLoggedIn && (
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="h6" sx={headingStyle}>
                                Explore
                            </Typography>
                            <MuiLink component={NavLink} to="/home" sx={linkStyle} onClick={() => handleSamePageLinkClick('/home')}>Home</MuiLink>
                            <MuiLink component={NavLink} to="/expenses" sx={linkStyle} onClick={() => handleSamePageLinkClick('/expenses')}>Expenses</MuiLink>
                            <MuiLink component={NavLink} to="/reports" sx={linkStyle} onClick={() => handleSamePageLinkClick('/reports')}>Reports</MuiLink>
                            <MuiLink component={NavLink} to="/faq" sx={linkStyle} onClick={() => handleSamePageLinkClick('/faq')}>FAQ</MuiLink>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={8} md={5}>
                        <Typography variant="h6" sx={headingStyle}>
                            Get In Touch
                        </Typography>
                        <Box sx={contactItemStyle}>
                            <EmailOutlinedIcon sx={{ color: 'common.white' }} fontSize="small" />
                                                    <MuiLink href="mailto:help@rsnap.ai" sx={{ ...linkStyle, mb: 0 }}>
                            help@rsnap.ai
                            </MuiLink>
                        </Box>
                        {/* App Store Badges - right aligned on desktop, centered on mobile */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: { xs: 'center', sm: 'flex-end' },
                            alignItems: 'center',
                            gap: 2,
                            mt: 2,
                          }}
                        >
                          <a href="https://play.google.com/store/apps/details?id=com.rsnap" target="_blank" rel="noopener noreferrer" >
                            <img
                              src={require('../assets/google-play-new.png')}
                              alt="Get it on Google Play"
                              style={{
                                height: 80,
                                width: 'auto',
                                display: 'block',
                                borderRadius: 8,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                transition: 'transform 0.2s',
                                cursor: 'pointer',
                              }}
                              onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.05)')}
                              onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
                            />
                          </a>
                          <a href="https://apps.apple.com/us/app/receiptsnap-ai/id6753581452" target="_blank" rel="noopener noreferrer" onClick={handleStoreClick}>
                            <img
                              src={require('../assets/app-store-new.png')}
                              alt="Download on the App Store"
                              style={{
                                height: 80,
                                width: 'auto',
                                display: 'block',
                                borderRadius: 8,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                transition: 'transform 0.2s',
                                cursor: 'pointer',
                              }}
                              onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.05)')}
                              onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
                            />
                          </a>
                        </Box>
                    </Grid>
                </Grid>
                <Divider sx={{ my: { xs: 3, sm: 4 }, borderColor: alpha(theme.palette.common.white, 0.15) }} />
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: { xs: 1.5, sm: 1 }
                }}>
                    <Typography variant="caption" sx={{ color: 'common.white', textAlign: { xs: 'center', sm: 'left' }, opacity: 0.8, fontSize: '0.75rem' }}>
                        © {new Date().getFullYear()} ReceiptSnap AI. All Rights Reserved.
                    </Typography>
                    <Box component="div" sx={{ color: 'common.white', textAlign: { xs: 'center', sm: 'right' } }}>
                        <MuiLink component={NavLink} to="/privacy" sx={bottomLinkStyle} target="_blank" rel="noopener noreferrer">Privacy Policy</MuiLink>
                        {' | '}
                        <MuiLink component={NavLink} to="/terms" sx={bottomLinkStyle} target="_blank" rel="noopener noreferrer">Terms & Conditions</MuiLink>
                    </Box>
                </Box>
            </Container>

        </Box>
    );
}

export default Footer;
